import {
  combineLatest,
  concat,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  firstValueFrom,
  last,
  lastValueFrom,
  map,
  mergeMap,
  Observable,
  Subscription,
  take,
  takeWhile,
  timeout,
} from 'rxjs';
import { v4 } from 'uuid';
import { MonitoringConfig } from './monitoring-config';
import { MotionMasterPubSubClient } from './motion-master-pub-sub-client';
import { MotionMasterPubSubSocket } from './motion-master-pub-sub-socket';
import { MotionMasterPubSubWebSocket } from './motion-master-pub-sub-web-socket';
import { MotionMasterReqResClient } from './motion-master-req-res-client';
import { MotionMasterReqResSocket } from './motion-master-req-res-socket';
import { MotionMasterReqResWebSocket } from './motion-master-req-res-web-socket';
import {
  mapMonitoringParameterValuesStatusMessageToDeviceParameters,
  mapMonitoringParameterValuesStatusMessageToParameterValues,
  selectMotionMasterMessageByTopic,
} from './operators';
import {
  DeviceParameterIds,
  DeviceRef,
  MotionMasterConnectionConfig,
  MotionMasterMessage,
  ParameterValueType,
  PositionProfileConfig,
  TorqueProfileConfig,
  VelocityProfileConfig,
} from './types';
import { DeviceParameter } from './device-parameter';
import { ModesOfOperation, Cia402State, getCia402State } from './cia402';
import { resolveAfter } from './util';
import { DataMonitoring } from './data-monitoring';
import { createHomingProcedureStatus, HomingProcedureConfig, HomingProcedureStatus } from './homing';

/**
 * Creates an instance of the {@link MotionMasterClient} using the specified hostname or connection configuration object.
 * Both the hostname and configuration properties are optional. Default values for each option can be found in {@link MotionMasterConnectionConfig}.
 * After the instance is created, the function opens the WebSocket connections, allowing the returned client object to send requests immediately.
 */
export function createMotionMasterClient(config?: Partial<MotionMasterConnectionConfig>): MotionMasterClient;
export function createMotionMasterClient(
  hostname?: string,
  pingSystemInterval?: number,
  systemAliveTimeout?: number
): MotionMasterClient;
export function createMotionMasterClient(
  configOrHostname?: Partial<MotionMasterConnectionConfig> | string,
  pingSystemInterval = 250,
  systemAliveTimeout = 1000
): MotionMasterClient {
  configOrHostname = configOrHostname ?? 'localhost';

  let clientId = v4();
  let hostname = typeof configOrHostname === 'string' ? configOrHostname : 'localhost';
  let reqResPort = 63524;
  let pubSubPort = 63525;
  let protocol = 'ws';
  let clientAliveTimeout: number | undefined;

  if (typeof configOrHostname === 'object') {
    hostname = configOrHostname.hostname ?? hostname;
    reqResPort = configOrHostname.reqResPort ?? reqResPort;
    pubSubPort = configOrHostname.pubSubPort ?? pubSubPort;
    clientId = configOrHostname.clientId ?? clientId;
    pingSystemInterval = configOrHostname.pingSystemInterval ?? pingSystemInterval;
    systemAliveTimeout = configOrHostname.systemAliveTimeout ?? systemAliveTimeout;
    protocol = configOrHostname.secure ? 'wss' : protocol;
    clientAliveTimeout = configOrHostname.clientAliveTimeout;
  }

  const reqResSocket = new MotionMasterReqResWebSocket();
  const pubSubSocket = new MotionMasterPubSubWebSocket();

  const client = new MotionMasterClient(reqResSocket, pubSubSocket);

  reqResSocket.open(
    `${protocol}://${hostname}:${reqResPort}?clientId=${clientId}`,
    pingSystemInterval,
    systemAliveTimeout
  );
  pubSubSocket.open(`${protocol}://${hostname}:${pubSubPort}?clientId=${clientId}`);

  if (clientAliveTimeout) {
    client
      .whenReady(5000)
      .then(() => {
        client.request.setSystemClientTimeout({ timeoutMs: clientAliveTimeout });
      })
      .catch((err) => {
        console.error('MotionMasterClient failed to set client alive timeout:', err.message);
      });
  }

  return client;
}

export class MotionMasterClient {
  readonly request: MotionMasterReqResClient;
  readonly monitor: MotionMasterPubSubClient;

  private systemEventSubscription?: Subscription;

  constructor(readonly reqResSocket: MotionMasterReqResSocket, readonly pubSubSocket: MotionMasterPubSubSocket) {
    this.request = new MotionMasterReqResClient(this.reqResSocket);
    this.monitor = new MotionMasterPubSubClient(this.pubSubSocket);

    // These observables are assigned here rather than via field initializers because they reference
    // `this.reqResSocket`/`this.pubSubSocket`. Under native class-field semantics (used by Bun, which
    // ignores `useDefineForClassFields`), field initializers run before the constructor body assigns
    // the parameter properties, leaving those sockets undefined. Assigning here guarantees correct order.
    this.socketsOpened$ = combineLatest([this.reqResSocket.opened$, this.pubSubSocket.opened$]).pipe(
      map(([reqResSocketOpened, pubSubSocketOpened]) => reqResSocketOpened && pubSubSocketOpened)
    );
    this.ready$ = this.socketsOpened$.pipe(
      distinctUntilChanged(),
      filter((opened) => opened === true)
    );
    this.onceReady$ = this.ready$.pipe(first());

    this.subscribeToSystemEvent();
  }

  /**
   * Emits true when both req/res and pub/sub sockets are opened.
   * Emits false if any socket closes.
   * This is a hot observable and always replays the latest opened state on subscription.
   */
  readonly socketsOpened$: Observable<boolean>;

  /**
   * Emits `true` **only when the client transitions into the ready state**,
   * meaning both sockets have become opened.
   *
   * `distinctUntilChanged()` guarantees that repeated `true` values are suppressed.
   * However, new subscribers will still immediately receive the last replayed `true`
   * if the client is already ready.
   */
  readonly ready$: Observable<boolean>;

  /**
   * Emits a single time when the client becomes ready for the first time.
   *
   * - Emits immediately if both sockets are already open.
   * - Completes after emitting once.
   * - If the client never becomes ready, this observable never emits.
   */
  readonly onceReady$: Observable<boolean>;

  /**
   * Returns an Observable that emits once when both req/res and pub/sub sockets are opened, then completes.
   *
   * - Emits immediately if both sockets are already open.
   * - Completes after emitting once.
   * - Errors with a TimeoutError if the specified timeout is reached before both sockets are open.
   *
   * @param requestTimeout The maximum time (in milliseconds) to wait for both sockets to open. Default is 5000 ms.
   * @returns Observable<void> that emits once when ready and then completes.
   */
  onceReady(requestTimeout = 5000): Observable<void> {
    return this.socketsOpened$.pipe(
      distinctUntilChanged(),
      filter((opened) => opened === true),
      take(1),
      timeout(requestTimeout),
      map(() => void 0)
    );
  }

  /**
   * Waits until both req/res and pub/sub sockets are opened.
   *
   * - Resolves immediately if both sockets are already open.
   * - Otherwise, waits up to `requestTimeout` milliseconds for both sockets to open.
   * - Throws a TimeoutError if the timeout is reached before both sockets are open.
   * - Returns a Promise that resolves when the client is ready.
   *
   * @param requestTimeout Maximum time in milliseconds to wait for both sockets to open. Default is 5000 ms.
   * @returns Promise<void> that resolves when both sockets are open or rejects on timeout.
   */
  whenReady(requestTimeout = 5000): Promise<void> {
    return firstValueFrom(
      this.socketsOpened$.pipe(
        distinctUntilChanged(),
        filter((opened) => opened === true),
        take(1),
        timeout(requestTimeout),
        map(() => void 0)
      )
    );
  }

  /**
   * Creates and returns and instance of {@link DataMonitoring} class.
   */
  createDataMonitoring(ids: DeviceParameterIds, monitoringInterval: number, config?: MonitoringConfig) {
    return new DataMonitoring(this, ids, monitoringInterval, config);
  }

  /**
   * Start monitoring.
   *
   * Note that the returned observable will never complete on its own.
   *
   * If you unsubscribe from it, the teardown code will send a request to the master to stop the monitoring.
   * That means you will end up with an observable that is not complete, but it won't emit any values.
   * This is fine when you want to use the monitoring observable directly in the template with async pipe,
   * which will unsubscribe automatically and stop the monitoring when you leave the component.
   *
   * If you need to complete the observable so that, for example, the toArray() operator works as expected, then use the {@link DataMonitoring} class,
   * which has a stop method. It can optionally buffer data for exporting as CSV or performing statistics on it.
   *
   * @param ids Device parameter IDs to monitor.
   * @param monitoringInterval At what interval should the master application send values.
   * @param config Configure the topic, buffer size, distinct.
   * @param requestTimeout How long will the client wait for the master to confirm that monitoring has started.
   * @returns An observable of an array of parameter values.
   */
  startMonitoring(
    ids: DeviceParameterIds,
    monitoringInterval: number,
    config?: MonitoringConfig,
    requestTimeout = 5000
  ): Observable<ParameterValueType[]> {
    return defer(() => {
      const topic = config?.topic ?? v4();
      const messageId = config?.messageId ?? v4();

      return this.request.resolveGetParameterValuesIds(ids).pipe(
        mergeMap((refs) => {
          if (new Set(refs.map(([deviceAddress]) => deviceAddress)).size !== 1) {
            throw new Error('Start monitoring only supports a single device');
          }

          // Skipping write-only (wo) parameters is necessary because Motion Master does not initiate monitoring when the requested list contains such parameters.
          // For now, this serves as a temporary solution until Motion Master is fixed.
          refs = refs.filter(([, index]) => index !== 0x1024);

          const deviceAddress = refs[0][0];

          const props: MotionMasterMessage.Request.IStartMonitoringDeviceParameterValues = {
            getDeviceParameterValues: {
              deviceAddress,
              parameters: refs.map(([, index, subindex]) => ({ index, subindex, loadFromCache: false })),
              sendProgress: false,
            },
            interval: monitoringInterval,
            topic,
          };

          return this.request.startMonitoringDeviceParameterValues(props, requestTimeout, messageId);
        }),
        mergeMap(() => {
          if (!config) {
            config = { messageId, topic, bufferSize: 1, distinct: false };
          }

          this.monitor.subscribe(config);

          const values$ = this.monitor.socket.data$.pipe(
            selectMotionMasterMessageByTopic(config.topic),
            mapMonitoringParameterValuesStatusMessageToParameterValues()
          );

          return new Observable<ParameterValueType[]>((observer) => {
            const subscription = values$.subscribe(observer).add(() => {
              this.stopMonitoring(messageId);
            });
            return subscription;
          });
        })
      );
    });
  }

  /**
   * TODO: return device parameters instead of just parameters
   * get parameters from ESI uprfront, and then when parameters are received
   * from motion master just concatenate (spread operator) props from ESI and props from parameter
   * assign device parameter id like 0x1018:00.12345
   * when assembling device parameter, get parameter from device in ESI based on product code, see ESI service
   * this should probably done for other get parameters functions, get ESI file from device or prepared, get data from it
   */
  startMonitoringDeviceParameters(
    ids: DeviceParameterIds,
    monitoringInterval: number,
    config?: MonitoringConfig,
    requestTimeout = 5000
  ): Observable<DeviceParameter[]> {
    return defer(() => {
      const topic = config?.topic ?? v4();
      const messageId = config?.messageId ?? v4();

      return this.request.resolveGetParameterValuesIds(ids).pipe(
        mergeMap((refs) => {
          if (new Set(refs.map(([deviceAddress]) => deviceAddress)).size !== 1) {
            throw new Error('Start monitoring only supports a single device');
          }

          // Skipping write-only (wo) parameters is necessary because Motion Master does not initiate monitoring when the requested list contains such parameters.
          // For now, this serves as a temporary solution until Motion Master is fixed.
          refs = refs.filter(([, index]) => index !== 0x1024);

          const deviceAddress = refs[0][0];

          const props: MotionMasterMessage.Request.IStartMonitoringDeviceParameterValues = {
            getDeviceParameterValues: {
              deviceAddress,
              parameters: refs.map(([, index, subindex]) => ({ index, subindex, loadFromCache: false })),
              sendProgress: false,
            },
            interval: monitoringInterval,
            topic,
          };

          return this.request.startMonitoringDeviceParameterValues(props, requestTimeout, messageId);
        }),
        mergeMap(() => {
          if (!config) {
            config = { messageId, topic, bufferSize: 1, distinct: false };
          }

          this.monitor.subscribe(config);

          const values$ = this.monitor.socket.data$.pipe(
            selectMotionMasterMessageByTopic(config.topic),
            mapMonitoringParameterValuesStatusMessageToDeviceParameters(this.request)
          );

          return new Observable<DeviceParameter[]>((observer) => {
            const subscription = values$.subscribe(observer).add(() => {
              this.stopMonitoring(messageId);
            });
            return subscription;
          });
        })
      );
    });
  }

  startMonitoringValue<T extends ParameterValueType = number>(
    id: string | [DeviceRef, number, number],
    monitoringInterval: number,
    config?: MonitoringConfig,
    requestTimeout = 5000
  ): Observable<T> {
    const ids = typeof id === 'string' ? [id] : [id];
    return this.startMonitoring(ids, monitoringInterval, config, requestTimeout).pipe(
      map((parameters) => (parameters[0] ?? 0) as T)
    );
  }

  trackControlword(deviceRef: DeviceRef, monitoringInterval = 10000): Observable<number> {
    return this.startMonitoringValue([deviceRef, 0x6040, 0], monitoringInterval).pipe(distinctUntilChanged());
  }

  trackStatusword(deviceRef: DeviceRef, monitoringInterval = 10000): Observable<number> {
    return this.startMonitoringValue([deviceRef, 0x6041, 0], monitoringInterval).pipe(distinctUntilChanged());
  }

  trackOperationEnabled(deviceRef: DeviceRef, monitoringInterval = 10000): Observable<boolean> {
    return this.trackStatusword(deviceRef, monitoringInterval).pipe(
      map((statusword) => getCia402State(statusword) === Cia402State.OPERATION_ENABLED)
    );
  }

  trackHomingProcedureStatus(deviceRef: DeviceRef, monitoringInterval = 50000): Observable<HomingProcedureStatus> {
    return this.trackStatusword(deviceRef, monitoringInterval).pipe(map(createHomingProcedureStatus));
  }

  trackTargetReached(deviceRef: DeviceRef, monitoringInterval = 10000): Observable<boolean> {
    return this.startMonitoringValue([deviceRef, 0x6041, 0], monitoringInterval).pipe(
      map((value) => (value & 0x0400) > 0), // tr = target reached bit
      distinctUntilChanged()
    );
  }

  waitUntilTargetReached(deviceRef: DeviceRef, monitoringInterval = 10000, requestTimeout = 5000): Observable<true> {
    return this.trackTargetReached(deviceRef, monitoringInterval).pipe(
      filter(Boolean),
      first(),
      timeout(requestTimeout)
    );
  }

  whenTargetReached(deviceRef: DeviceRef, monitoringInterval = 10000, requestTimeout = 5000): Promise<true> {
    return firstValueFrom(this.waitUntilTargetReached(deviceRef, monitoringInterval, requestTimeout));
  }

  waitUntilCia402StateReached(
    deviceRef: DeviceRef,
    state: Cia402State,
    monitoringInterval = 10000,
    requestTimeout = 60000
  ): Observable<void> {
    return this.startMonitoringValue([deviceRef, 0x6041, 0], monitoringInterval).pipe(
      filter((value) => getCia402State(value) === state),
      first(),
      timeout(requestTimeout),
      map(() => undefined)
    );
  }

  whenCia402StateReached(
    deviceRef: DeviceRef,
    state: Cia402State,
    monitoringInterval = 10000,
    requestTimeout = 60000
  ): Promise<void> {
    return firstValueFrom(this.waitUntilCia402StateReached(deviceRef, state, monitoringInterval, requestTimeout));
  }

  waitUntilParameterValueEquals(
    id: string | [DeviceRef, number, number],
    value: number,
    window = 0,
    monitoringInterval = 50000,
    requestTimeout = 5000
  ): Observable<true> {
    return this.startMonitoringValue(id, monitoringInterval).pipe(
      distinctUntilChanged(),
      map((v) => {
        return v >= value - window && v <= value + window;
      }),
      filter(Boolean),
      timeout(requestTimeout)
    );
  }

  whenParameterValueEquals(
    id: string | [DeviceRef, number, number],
    value: number,
    window = 0,
    monitoringInterval = 20000,
    requestTimeout = 5000
  ): Promise<true> {
    return firstValueFrom(this.waitUntilParameterValueEquals(id, value, window, monitoringInterval, requestTimeout));
  }

  /**
   * Executes a torque profile on the specified device.
   *
   * Downloads target torque and slope first, then sets Torque Profile Mode and transitions to
   * Operation Enabled. The brake is released automatically by the drive in PT mode. What happens
   * next depends on the combination of skipQuickStop and targetReachTimeout:
   *
   * - skipQuickStop: false (default) + targetReachTimeout set:
   *   Waits for the target reached bit (0x6041) within the given timeout, waits for holdingDuration
   *   if set, then issues a quick stop. The state waited for after the quick stop depends on the
   *   quick stop option code (0x605A): Quick Stop Active if the code is 6, Switch On Disabled otherwise.
   *   Throws if the target is not reached in time.
   *
   * - skipQuickStop: false (default) + no positive targetReachTimeout:
   *   Throws immediately — the function cannot determine when to issue the quick stop.
   *
   * - skipQuickStop: true + targetReachTimeout set:
   *   Waits for the target reached bit within the given timeout but does not issue a quick stop.
   *   The device remains in the Operation Enabled state.
   *
   * - skipQuickStop: true + no targetReachTimeout:
   *   Returns immediately after enabling the drive. The device remains in the Operation Enabled state.
   *
   * When skipQuickStop is true and an error is thrown (e.g. target not reached in time), the drive
   * remains in Operation Enabled. The caller is responsible for issuing a quick stop or transitioning
   * the drive to a safe state.
   *
   * @param deviceRef - Reference to the target device.
   * @param config - Torque profile configuration.
   * @throws Error if skipQuickStop is false and targetReachTimeout is not a positive number.
   * @throws Error if the target is not reached within targetReachTimeout.
   */
  async runTorqueProfile(deviceRef: DeviceRef, config: TorqueProfileConfig): Promise<void> {
    if (!config.skipQuickStop && (config.targetReachTimeout == null || config.targetReachTimeout <= 0)) {
      throw new Error('targetReachTimeout is required when skipQuickStop is false');
    }

    await this.request.download(deviceRef, 0x6071, 0, config.target);
    await this.request.download(deviceRef, 0x6087, 0, config.slope);

    if (config.window !== undefined) {
      await this.request.download(deviceRef, 0x2014, 1, config.window);
    }

    if (config.windowTime !== undefined) {
      await this.request.download(deviceRef, 0x2014, 2, config.windowTime);
    }

    await lastValueFrom(this.request.setModesOfOperation(deviceRef, ModesOfOperation.TORQUE_PROFILE_MODE));
    await this.request.transitionToCia402State(deviceRef, Cia402State.OPERATION_ENABLED);

    if (config.targetReachTimeout && config.targetReachTimeout > 0) {
      try {
        await this.whenTargetReached(deviceRef, config.monitoringInterval ?? 1000, config.targetReachTimeout);
        if (config.holdingDuration && config.holdingDuration > 0) {
          await resolveAfter(config.holdingDuration);
        }
      } finally {
        if (!config.skipQuickStop) {
          const quickStopOptionCode = await this.request.upload(deviceRef, 0x605a, 0);
          await this.request.quickStop(deviceRef);
          const targetState = quickStopOptionCode === 6 ? Cia402State.QUICK_STOP_ACTIVE : Cia402State.SWITCH_ON_DISABLED;
          await this.whenCia402StateReached(deviceRef, targetState);
        }
      }
    }
  }

  /**
   * Executes a velocity profile on the specified device.
   *
   * Downloads target velocity, acceleration and deceleration first, then sets Profile Velocity Mode
   * and transitions to Operation Enabled. The brake is released automatically by the drive in PV mode.
   * What happens next depends on the combination of skipQuickStop and targetReachTimeout:
   *
   * - skipQuickStop: false (default) + targetReachTimeout set:
   *   Waits for the target reached bit (0x6041) within the given timeout, waits for holdingDuration
   *   if set, then issues a quick stop. The state waited for after the quick stop depends on the
   *   quick stop option code (0x605A): Quick Stop Active if the code is 6, Switch On Disabled otherwise.
   *   Throws if the target is not reached in time.
   *
   * - skipQuickStop: false (default) + no positive targetReachTimeout:
   *   Throws immediately — the function cannot determine when to issue the quick stop.
   *
   * - skipQuickStop: true + targetReachTimeout set:
   *   Waits for the target reached bit within the given timeout but does not issue a quick stop.
   *   The device remains in the Operation Enabled state.
   *
   * - skipQuickStop: true + no targetReachTimeout:
   *   Returns immediately after enabling the drive. The device remains in the Operation Enabled state.
   *
   * When skipQuickStop is true and an error is thrown (e.g. target not reached in time), the drive
   * remains in Operation Enabled. The caller is responsible for issuing a quick stop or transitioning
   * the drive to a safe state.
   *
   * @param deviceRef - Reference to the target device.
   * @param config - Velocity profile configuration.
   * @throws Error if skipQuickStop is false and targetReachTimeout is not a positive number.
   * @throws Error if the target is not reached within targetReachTimeout.
   */
  async runVelocityProfile(deviceRef: DeviceRef, config: VelocityProfileConfig): Promise<void> {
    if (!config.skipQuickStop && (config.targetReachTimeout == null || config.targetReachTimeout <= 0)) {
      throw new Error('targetReachTimeout is required when skipQuickStop is false');
    }

    await this.request.download(deviceRef, 0x60ff, 0, config.target);
    await this.request.download(deviceRef, 0x6083, 0, config.acceleration);
    await this.request.download(deviceRef, 0x6084, 0, config.deceleration);

    if (config.window !== undefined) {
      await this.request.download(deviceRef, 0x606d, 0, config.window);
    }

    if (config.windowTime !== undefined) {
      await this.request.download(deviceRef, 0x606e, 0, config.windowTime);
    }

    await lastValueFrom(this.request.setModesOfOperation(deviceRef, ModesOfOperation.PROFILE_VELOCITY_MODE));
    await this.request.transitionToCia402State(deviceRef, Cia402State.OPERATION_ENABLED);

    if (config.targetReachTimeout && config.targetReachTimeout > 0) {
      try {
        await this.whenTargetReached(deviceRef, config.monitoringInterval ?? 1000, config.targetReachTimeout);
        if (config.holdingDuration && config.holdingDuration > 0) {
          await resolveAfter(config.holdingDuration);
        }
      } finally {
        if (!config.skipQuickStop) {
          const quickStopOptionCode = await this.request.upload(deviceRef, 0x605a, 0);
          await this.request.quickStop(deviceRef);
          const targetState = quickStopOptionCode === 6 ? Cia402State.QUICK_STOP_ACTIVE : Cia402State.SWITCH_ON_DISABLED;
          await this.whenCia402StateReached(deviceRef, targetState);
        }
      }
    }
  }

  /**
   * Executes a position profile on the specified device.
   *
   * Downloads target position, velocity, acceleration and deceleration first, then sets Profile
   * Position Mode, transitions to Operation Enabled and applies the set point. The brake is released
   * automatically by the drive in PP mode. What happens next depends on the combination of
   * skipQuickStop and targetReachTimeout:
   *
   * - skipQuickStop: false (default) + targetReachTimeout set:
   *   Waits for the target reached bit (0x6041) within the given timeout, waits for holdingDuration
   *   if set, then issues a quick stop. The state waited for after the quick stop depends on the
   *   quick stop option code (0x605A): Quick Stop Active if the code is 6, Switch On Disabled otherwise.
   *   Throws if the target is not reached in time.
   *
   * - skipQuickStop: false (default) + no positive targetReachTimeout:
   *   Throws immediately — the function cannot determine when to issue the quick stop.
   *
   * - skipQuickStop: true + targetReachTimeout set:
   *   Waits for the target reached bit within the given timeout but does not issue a quick stop.
   *   The device remains in the Operation Enabled state.
   *
   * - skipQuickStop: true + no targetReachTimeout:
   *   Returns immediately after applying the set point. The device remains in the Operation Enabled state.
   *
   * When skipQuickStop is true and an error is thrown (e.g. target not reached in time), the drive
   * remains in Operation Enabled. The caller is responsible for issuing a quick stop or transitioning
   * the drive to a safe state.
   *
   * @param deviceRef - Reference to the target device.
   * @param config - Position profile configuration.
   * @throws Error if skipQuickStop is false and targetReachTimeout is not a positive number.
   * @throws Error if the target is not reached within targetReachTimeout.
   */
  async runPositionProfile(deviceRef: DeviceRef, config: PositionProfileConfig): Promise<void> {
    if (!config.skipQuickStop && (config.targetReachTimeout == null || config.targetReachTimeout <= 0)) {
      throw new Error('targetReachTimeout is required when skipQuickStop is false');
    }

    if (config.relative) {
      const positionActualValue = await this.request.upload(deviceRef, 0x6064, 0);
      await this.request.download(deviceRef, 0x607a, 0, config.target + positionActualValue);
    } else {
      await this.request.download(deviceRef, 0x607a, 0, config.target);
    }

    await this.request.download(deviceRef, 0x6081, 0, config.velocity);
    await this.request.download(deviceRef, 0x6083, 0, config.acceleration);
    await this.request.download(deviceRef, 0x6084, 0, config.deceleration);

    if (config.window !== undefined) {
      await this.request.download(deviceRef, 0x6067, 0, config.window);
    }

    if (config.windowTime !== undefined) {
      await this.request.download(deviceRef, 0x6068, 0, config.windowTime);
    }

    await lastValueFrom(this.request.setModesOfOperation(deviceRef, ModesOfOperation.PROFILE_POSITION_MODE));
    await this.request.transitionToCia402State(deviceRef, Cia402State.OPERATION_ENABLED);

    await this.request.applySetPoint(deviceRef);

    if (config.targetReachTimeout && config.targetReachTimeout > 0) {
      try {
        await this.whenTargetReached(deviceRef, config.monitoringInterval ?? 1000, config.targetReachTimeout);
        if (config.holdingDuration && config.holdingDuration > 0) {
          await resolveAfter(config.holdingDuration);
        }
      } finally {
        if (!config.skipQuickStop) {
          const quickStopOptionCode = await this.request.upload(deviceRef, 0x605a, 0);
          await this.request.quickStop(deviceRef);
          const targetState = quickStopOptionCode === 6 ? Cia402State.QUICK_STOP_ACTIVE : Cia402State.SWITCH_ON_DISABLED;
          await this.whenCia402StateReached(deviceRef, targetState);
        }
      }
    }
  }

  /**
   * Executes the homing procedure to locate the home position value (0x2005:01).
   *
   * You can optionally provide a configuration object that specifies acceleration, method, and speeds;
   * if not provided, the parameter values already set on the device will be used.
   *
   * The function will first emit a "running" status, followed by either "succeeded" or "failed".
   * After completion, the device will remain in the Operation Enabled state.
   *
   * No CiA402 state transitions will occur for the "Homing on current position" methods (35 and 37).
   * Additionally, the halt bit will remain unchanged for these methods, in contrast to other methods where the halt bit is cleared.
   *
   * @param deviceRef - Reference to the device being homed.
   * @param config - Optional configuration for the homing procedure.
   * @param monitoringInterval - Interval for monitoring the homing procedure status in microseconds (default: 50,000 μs).
   * @param requestTimeout - Timeout for the homing request in milliseconds (default: 300,000 ms).
   * @returns An Observable that emits the status of the homing procedure.
   */
  runHomingProcedure(
    deviceRef: DeviceRef,
    config?: HomingProcedureConfig,
    monitoringInterval = 50000,
    requestTimeout = 300000
  ): Observable<HomingProcedureStatus> {
    return concat(
      config?.acceleration ? this.request.download(deviceRef, 0x609a, 0, config.acceleration) : EMPTY,
      config?.method ? this.request.download(deviceRef, 0x6098, 0, config.method) : EMPTY,
      config?.speedDuringSearchForSwitch
        ? this.request.download(deviceRef, 0x6099, 1, config.speedDuringSearchForSwitch)
        : EMPTY,
      config?.speedDuringSearchForZero
        ? this.request.download(deviceRef, 0x6099, 2, config.speedDuringSearchForZero)
        : EMPTY,
      config ? defer(() => this.request.forceOnDemandParametersUpdate(deviceRef)) : EMPTY,
      this.request.getParameterValue(deviceRef, 0x6098, 0) // retrieve the current value of the homing method from the device
    ).pipe(
      last(), // emit only the value of the homing method
      mergeMap((method) => {
        if (method === 35 || method === 37) {
          return concat(
            this.request.setModesOfOperation(deviceRef, ModesOfOperation.HOMING_MODE),
            this.request.setControlwordToStartHoming(deviceRef, false)
          ).pipe(last());
        } else {
          return concat(
            this.request.setModesOfOperation(deviceRef, ModesOfOperation.HOMING_MODE),
            defer(() => this.request.setControlwordToStartHoming(deviceRef, true)),
            defer(() => this.request.transitionToCia402State(deviceRef, Cia402State.OPERATION_ENABLED))
          ).pipe(last());
        }
      }),
      mergeMap(() => {
        return this.trackHomingProcedureStatus(deviceRef, monitoringInterval).pipe(
          takeWhile((status) => status.request === 'running', true),
          timeout(requestTimeout)
        );
      })
    );
  }

  subscribeToSystemEvent() {
    this.systemEventSubscription = this.monitor.systemEvent$.subscribe((systemEvent) => {
      if (systemEvent.state === MotionMasterMessage.Status.SystemEvent.State.DEINITIALIZING) {
        this.request.clearDevicesInformation();
      }
    });
  }

  stopMonitoring(messageId: string): void {
    this.request.stopMonitoringDeviceParameterValues({ startMonitoringRequestId: messageId });
    this.monitor.unsubscribe(messageId);
  }

  stopMonitoringAll(): void {
    this.monitor.messageIds.forEach(this.stopMonitoring);
  }

  closeSockets() {
    this.systemEventSubscription?.unsubscribe();
    this.request.clearDevicesInformation();

    this.reqResSocket.close();
    this.pubSubSocket.close();
  }

  reopenSockets() {
    this.reqResSocket.reopen();
    this.pubSubSocket.reopen();

    this.subscribeToSystemEvent();
  }
}
